/**
 * CODE-C Readiness-Resistance Assessment — Scoring Engine v3
 *
 * Formulas per Clutch_Assessment_Platform_Build_Guide_v5D:
 *
 * Readiness per dimension:
 *   - Reverse-code R-items: recoded = 6 − raw
 *   - avg all 5 items (using recoded values for R-items)
 *   - readiness_pct = Math.round((avg − 1) / 4 × 100)
 *
 * Resistance per dimension:
 *   - Use RAW values of the 2 R-items only
 *   - avg those 2 raw values
 *   - resistance_pct = Math.round((avg − 1) / 4 × 100)
 *
 * Overall indices = simple average of the 5 dimension scores.
 */

import type { CodecScoreOutput, ResponseMap, ScoreBand } from '../types'
import { CODEC_ITEMS, CODEC_DIMS } from '../data/codec-items'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pctScore(values: (number | null)[]): number | null {
  const filled = values.filter((v): v is number => v !== null)
  if (filled.length === 0) return null
  const avg = filled.reduce((s, v) => s + v, 0) / filled.length
  return Math.round(((avg - 1) / 4) * 100)
}

export function getBand(score: number | null): ScoreBand {
  if (score === null) return null
  if (score >= 75) return 'strong'
  if (score >= 50) return 'developing'
  return 'needs'
}

export function getBandLabel(band: ScoreBand): string {
  switch (band) {
    case 'strong':     return 'Strong'
    case 'developing': return 'Developing'
    case 'needs':      return 'Needs Support'
    default:           return '—'
  }
}

// ─── Main scoring function ────────────────────────────────────────────────────

export function scoreCodec(responses: ResponseMap): CodecScoreOutput {
  const dimScores: CodecScoreOutput['dims'] = {} as CodecScoreOutput['dims']
  const allReadiness: number[] = []
  const allResistance: number[] = []

  for (const dimMeta of CODEC_DIMS) {
    const dimItems = CODEC_ITEMS.filter(item => item.dim === dimMeta.key)

    // Readiness: use all 5 values, recode R-items
    const readinessVals = dimItems.map(item => {
      const raw = responses[item.id] ?? null
      if (raw === null) return null
      return item.reverseCoded ? (6 - raw) : raw
    })

    // Resistance: use RAW values of only the 2 R-items
    const resistanceVals = dimItems
      .filter(item => item.reverseCoded)
      .map(item => responses[item.id] ?? null)

    const readiness = pctScore(readinessVals)
    const resistance = pctScore(resistanceVals)

    if (readiness !== null) allReadiness.push(readiness)
    if (resistance !== null) allResistance.push(resistance)

    dimScores[dimMeta.key] = {
      readiness,
      resistance,
      band: getBand(readiness),
    }
  }

  const overallReadiness =
    allReadiness.length > 0
      ? Math.round(allReadiness.reduce((a, b) => a + b, 0) / allReadiness.length)
      : null

  const overallResistance =
    allResistance.length > 0
      ? Math.round(allResistance.reduce((a, b) => a + b, 0) / allResistance.length)
      : null

  return {
    dims: dimScores,
    overallReadiness,
    overallResistance,
    overallBand: getBand(overallReadiness),
    version: 'v3.0',
  }
}

// ─── Count helpers ────────────────────────────────────────────────────────────

export function countAnswered(responses: ResponseMap): number {
  return CODEC_ITEMS.filter(item => responses[item.id] != null).length
}

export const CODEC_TOTAL_ITEMS = CODEC_ITEMS.length  // 25
